import {
	AfterViewInit,
	Component,
	HostListener,
	Inject,
	OnDestroy,
	OnInit,
	signal,
} from '@angular/core';
import {
	MatDialog,
	MatDialogRef,
	MAT_DIALOG_DATA,
} from '@angular/material/dialog';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import {
	convertCloserAddressToLLCAddress,
	isAddressSet,
} from '@core/util/address';
import { isSet } from '@core/util/value';
import {
	BaseClient,
	Guarantor,
	Mortgagor,
	Property,
	TransactionData,
	Identification as LLCIdentifications,
	Signatory,
	FireInsurancePolicy,
	TitleInsurancePolicy,
	Attorney,
	Incorporation,
} from '@fnModels/llc-pushtransaction';
import { LLCFunctions } from '@functions/llc.service';
import {
	AssociatedContactType,
	BusinessClient,
	ClientType,
	PersonClient,
	RepresentationType,
} from '@models/clients';
import { Identification } from '@models/identification';
import { BusinessUser } from '@models/businesses';
import { LLCTransaction } from '@models/llc-mortgages';
import {
	BusinessMatterClient,
	BusinessMatterClientWithAssociations,
	CondoUnit,
	Matter,
	MatterAdjustment,
	MatterAdjustmentTarion,
	MatterClientAssociations,
	MatterFileType,
	MatterTitle,
	PersonMatterClient,
	PersonMatterClientWithAssociations,
	PropertyStatus,
	PropertyType,
	SpousalStatus,
	UnitType,
} from '@models/matter';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SelectSnapshot, ViewSelectSnapshot } from '@ngxs-labs/select-snapshot';
import { Select } from '@ngxs/store';
import { AuthState } from '@state/auth';
import { BusinessUsersState } from '@state/business-users/business-users.state';
import {
	LLCDataSubmitObjects,
	LLCSelectors,
} from '@state/global/llc_submitData';
import { LLCMortgageDispatch } from '@state/mortgages/llc-mortgage/llc-mortgage.dispatch';
import { LLCMortgageState } from '@state/mortgages/llc-mortgage/llc-mortgage.state';
import { cloneDeep, isNumber } from 'lodash';
import LogRocket from 'logrocket';
import { Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { Ids } from 'src/app/shared/data/Identification';
import { ICON } from '@core/util/icon';
import {
	getLegalDescription,
	getLLCLenderType,
	GetMortgageTitleInsurancePolicies,
	LLCLenderType,
	LlcService,
} from 'src/app/shared/services/mortgages/llc.service';
import { LLCAmendmentsModalComponent } from '../amendments/amendments.component';
import {
	DownloadDocumentsComponent,
	DownloadLLCDocumentsData,
} from '../download-documents/download-documents.component';
import {
	PortalUnsavedChangesCloseData,
	PortalUnsavedChangesComponent,
} from '../portal-unsaved-changes/portal-unsaved-changes.component';
import { centsToDollars } from '@core/util/money';
import { AdjustmentsSelector } from '@state/matter/adjustments/adjustments.selectors';
import {
	MatterMortgage,
	MatterMortgagePriority,
	MatterMortgageWithAssociations,
} from '@models/mortgage';
import {
	GetRequestForFundsPins,
	closerEstateTypeToLLCType,
} from '@core/util/llc';
import { LROData } from 'src/app/shared/data/LRO';
import { AmendmentModalData } from '../amendments/ammendments.models';
import { TitleInsurancePolicyType } from '@models/title-insurance';
import { getMortgageLenderName } from '@core/util/mortgage';
import { environment } from 'src/environments/environment';
import { envTypes } from 'src/environments/env.types';
import { clientLegalName } from '@core/util/client';
import { NameFormattingSettings } from '@models/businessSettings/settings';
import { BusinessState } from '@state/business';
import { MatterTitlesState } from '@state/matter/titles/titles.state';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { Jurisdiction } from '@models/jurisdictions';
import { MatterJurisdictionSelectors } from '@state/matter/jurisdictions/jurisdictions.selectors';
import {
	BaseTransactionAddress,
	ClientMappingType,
	ClientMappings,
	MortgageInstructionLender,
	MortgageInstructionPerson,
} from '@models/mortgages/mortgage-base';
import { getClientBrokerageFromMatter } from '@state/utils/helper';
import { ErrorCodes } from '@fnModels/error-codes';
import { FirebaseError } from 'firebase/app';

export interface PortalIFrameData {
	transaction: LLCTransaction;
	matterMortgage?: MatterMortgageWithAssociations;
	readonlyMode?: boolean;
}

export const LegalDescriptionLimit = 4000;

@UntilDestroy()
@Component({
	selector: 'app-portal-iframe',
	templateUrl: './portal-iframe.component.html',
	styleUrls: ['./portal-iframe.component.scss'],
	standalone: false,
})
export class PortalIframeComponent implements OnInit, AfterViewInit, OnDestroy {
	loaded = false;
	transaction: LLCTransaction;
	unsafeUrl: string;
	url: SafeResourceUrl;
	data: PortalIFrameData;
	errorMessage_ = signal('');
	ICON = ICON;
	loading = true;
	readOnlyMode = false;
	issues: string[] = [];
	showIssues = false;
	hasAmendments = false;
	clients: (PersonClient | BusinessClient)[] = [];
	loadingText = 'Loading LLC Portal';
	fetchStatusError = false;
	otherUserLocked = false;
	otherUserLockedName;
	otherUserLockedEmail;
	closing = false;
	timer: NodeJS.Timeout;

	@Select(AdjustmentsSelector.tarionWarrantyAdjustment)
	tarionWarrantyAdjustment$: Observable<MatterAdjustmentTarion>;

	@ViewSelectSnapshot(SelectedMatter.getSelected) matter: Matter;

	@Select(LLCSelectors.getSubmitDataObjects)
	submitDataObject$: Observable<LLCDataSubmitObjects>;
	@SelectSnapshot(AuthState.getUserId) userId: string;
	@Select(BusinessUsersState.getUser) getUserFn$: Observable<
		(id: string) => BusinessUser
	>;
	@SelectSnapshot(BusinessState.getNameFormattingSettings)
	nameFormattingSettings: NameFormattingSettings;
	@Select(LLCMortgageState.getLLCMortgage) getMortgageFn$: Observable<
		(id: string) => LLCTransaction
	>;
	@SelectSnapshot(MatterTitlesState.getAll) matterTitles: MatterTitle[];
	@SelectSnapshot(SelectedMatter.titleInsuranceEffectiveDate)
	titleInsuranceEffectiveDate: string;
	@SelectSnapshot(SelectedMatter.isCondoUnit) isCondoUnit: boolean;
	@SelectSnapshot(MatterJurisdictionSelectors.getPrimary)
	primaryJurisdiction: Jurisdiction;

	//this is incase the browser is closed
	@HostListener('window:beforeunload', ['$event'])
	beforeUnloadHandler() {
		if (!this.readOnlyMode) {
			this.closing = true;
			this.llcTransactionDispatch.UnlockUserAccess(this?.data?.transaction?.id);
			// this.triggerDocumentFetch('tab close');
		}
	}

	constructor(
		public dialogRef: MatDialogRef<PortalIframeComponent>,
		@Inject(MAT_DIALOG_DATA) public _data: PortalIFrameData,
		private dialog: MatDialog,
		private sanitizer: DomSanitizer,
		private llcFunctions: LLCFunctions,
		private llcTransactionDispatch: LLCMortgageDispatch,
		private llcService: LlcService,
	) {
		this.data = _data;
	}

	ngOnDestroy(): void {
		if (!this.readOnlyMode) {
			this.closing = true;
			this.llcTransactionDispatch.UnlockUserAccess(this.data.transaction?.id);
			// this.triggerDocumentFetch('modal close');
		}

		if (this.timer) {
			clearTimeout(this.timer);
		}
	}

	ngAfterViewInit(): void {
		this.dialogRef.updateSize('90%', '90%');
	}

	ngOnInit(): void {
		window.onmessage = async (ev: MessageEvent<unknown>) => {
			if (ev?.data === 'dirty') {
				this.loading = false;

				const unsavedChangesDialog = this.dialog.open(
					PortalUnsavedChangesComponent,
					{},
				);
				unsavedChangesDialog
					.afterClosed()
					.subscribe(async (unsavedData: PortalUnsavedChangesCloseData) => {
						if (!unsavedData.stayOnPage) {
							await this.onClose();
						}
					});
			} else if (ev?.data === 'untouched') {
				await this.onClose();
			}
		};
		this.getTransaction(this.data.transaction.id).subscribe(
			async (_transaction: LLCTransaction) => {
				this.transaction = _transaction;
				if (!this.loaded && _transaction) {
					this.loaded = true;
					this.readOnlyMode = isSet(this.data.readonlyMode)
						? this.data.readonlyMode
						: false;

					if (this.readOnlyMode) {
						const _url = await this.llcFunctions
							.getRedirectUrl('LLCDEALRO', _transaction.id)
							.toPromise();
						this.url = this.sanitizer.bypassSecurityTrustResourceUrl(_url.url);
						this.unsafeUrl = _url.url;
						this.loading = false;
					} else {
						//check if there are any ammendments. If so then send to ammendment modal
						this.attemptToSubmitData();
					}
				}
			},
		);

		this.dialogRef.disableClose = true;

		this.dialogRef
			.backdropClick()
			.pipe(untilDestroyed(this))
			.subscribe(async () => {
				await this.beforeClose();
			});
	}

	async onClose() {
		try {
			this.loading = true;
			this.loadingText = 'Updating mortgage status';
			// const status = (await this.llcFunctions
			// 	.updateTransactionStatus(this.transaction.id)
			// 	.toPromise()) as TransactionStatusBase;

			// if (status.status === MortgageTransactionStatus.COMPLETED) {
			const downloadDocsData: DownloadLLCDocumentsData = {
				transactionId: this.transaction.id,
				matterMortgageId: this.data?.matterMortgage?.id,
				matterId: this.matter?.id,
			};

			this.dialog.open(DownloadDocumentsComponent, {
				data: downloadDocsData,
			});
			// }
		} catch {
			this.fetchStatusError = true;
			console.log(
				`Unable to fetch status of the transaction: ${this.transaction?.id}, fct source urn: ${this.transaction?.overview?.sourceFctUrn}, lender ref number: ${this.transaction?.overview?.lenderReferenceNumber}, lender code: ${this.transaction?.overview?.lenderCode}`,
			);
			LogRocket.error(
				`Unable to fetch status of the transaction: ${this.transaction?.id}, fct source urn: ${this.transaction?.overview?.sourceFctUrn}, lender ref number: ${this.transaction?.overview?.lenderReferenceNumber}, lender code: ${this.transaction?.overview?.lenderCode}`,
			);
		}

		this.dialogRef.close();
	}

	async beforeClose() {
		const iframe = document.querySelector('#llc-portal-iframe');
		if (iframe) {
			(iframe as HTMLIFrameElement).contentWindow.postMessage(
				'OK',
				this.unsafeUrl,
			);
			this.loadingText = 'Checking LLC for unsaved changes.';
		} else {
			await this.onClose();
		}
	}

	updateTimeLock() {
		this.timer = setTimeout(() => {
			if (!this.closing) {
				this.llcTransactionDispatch.UpdateTimestamp(this.transaction.id);
				this.updateTimeLock();
			}
		}, 10000);
	}

	getTransaction(id: string) {
		return this.getMortgageFn$.pipe(
			map((fn) => fn(id)),
			untilDestroyed(this),
		);
	}

	getUser(id: string) {
		return this.getUserFn$.pipe(
			map((fn) => fn(id)),
			take(1),
		);
	}

	attemptToSubmitData() {
		this.submitDataObject$.pipe(take(1)).subscribe(async (result) => {
			// validate user can send data
			try {
				this.issues = this.validateSendTransactionData(
					result.matter,
					result.mortgagors,
					this.transaction,
				);
				this.readOnlyMode = this.issues.length > 0;

				const access = this.readOnlyMode ? 'LLCDEALRO' : 'LLCDEAL';
				let _url = await this.llcFunctions
					.getRedirectUrl(access, this.transaction.id)
					.toPromise();

				if (access === 'LLCDEAL' && _url.lenderChanges) {
					this.hasAmendments = true;
				}

				if (!_url?.url) {
					_url = await this.llcFunctions
						.getRedirectUrl('LLCDEALRO', this.transaction.id)
						.toPromise();
					this.readOnlyMode = true;
				}

				const currentTime = new Date().getTime();
				const timeTreshold = currentTime - 2 * 60000;

				if (
					!this.readOnlyMode &&
					this.transaction?.lastAccessUserId !== this.userId &&
					isSet(this.transaction?.lastAccessUserId) &&
					this.transaction.lastAccessTime > timeTreshold
				) {
					this.readOnlyMode = true;
					this.otherUserLocked = true;
					const user = await this.getUser(
						this.transaction?.lastAccessUserId,
					).toPromise();
					this.otherUserLockedName = `${user.firstName} ${user.lastName}`;
					this.otherUserLockedEmail = user.email;
					_url = await this.llcFunctions
						.getRedirectUrl('LLCDEALRO', this.transaction.id)
						.toPromise();
				}

				if (!this.readOnlyMode) {
					this.llcTransactionDispatch.LockUserAccess(this.transaction.id);
					this.updateTimeLock();
					const transactionData = this.buildTransactionData(
						result.matter,
						this.data.transaction,
						this.data.matterMortgage,
						result.mortgagors,
						result.guarantors,
						result.tarionWarrantyAdjustment,
						this.nameFormattingSettings,
						this.matterTitles,
						this.primaryJurisdiction,
					);

					LogRocket.info(
						`Data sent to LLC - ${this.transaction.id}, fct source urn: ${this.transaction?.overview?.sourceFctUrn}, lender ref number: ${this.transaction?.overview?.lenderReferenceNumber}, lender code: ${this.transaction?.overview?.lenderCode} - `,
						transactionData,
					);

					if (environment.env !== envTypes.PRODUCTION) {
						console.log('Data sent to LLC', transactionData);
					}

					await this.llcFunctions
						.sendTransactionData(this.transaction.id, transactionData)
						.toPromise();
				}

				this.unsafeUrl = _url.url;
				this.url = this.sanitizer.bypassSecurityTrustResourceUrl(_url.url);
			} catch (err) {
				this.errorHandler(err);
			} finally {
				this.loading = false;
			}
		});
	}

	viewAmendments() {
		const data: AmendmentModalData = {
			transaction: this.data.transaction,
		};
		this.dialog.open(LLCAmendmentsModalComponent, { data });
		this.dialogRef.close();
	}

	validateSendTransactionData = (
		matter: Matter,
		mortgagors: (
			| PersonMatterClientWithAssociations
			| BusinessMatterClientWithAssociations
		)[],
		mortgage: LLCTransaction,
	): string[] => {
		const issues: string[] = [];

		if (!isSet(matter?.number)) {
			issues.push('Missing matter number');
		}

		// check the users have been mapped
		if (!this.validateMortgagerGuarantorMappingComplete(mortgage)) {
			issues.push('Incorrect number of mortgagors and/or guarantors');
		}

		//validate all user data is entered
		mortgagors.forEach((client) => {
			if (client.type === ClientType.PERSON) {
				const _clientIssues = this.verifyPerson(client as PersonMatterClient);
				if (_clientIssues.length > 0) {
					issues.push(
						..._clientIssues.map(
							(x) => `${(client as PersonMatterClient).firstName}: ${x}`,
						),
					);
				}
			} else {
				const _companyIssues = this.verifyCompany(
					client as BusinessMatterClient,
				);
				if (_companyIssues.length > 0) {
					issues.push(
						..._companyIssues.map(
							(x) => `${(client as BusinessMatterClient).name}: ${x}`,
						),
					);
				}
			}
		});

		if (!matter?.propertyDetails?.interestEstate) {
			issues.push('Missing property estate type');
		}

		if (
			!(
				matter?.propertyDetails?.address &&
				isAddressSet(matter?.propertyDetails?.address)
			)
		) {
			issues.push('Property address is incomplete');
		}

		if (issues.length > 0) {
			LogRocket.info('Issues with transaction', issues);
		}

		return issues;
	};

	validateMortgagerGuarantorMappingComplete = (mortgage: LLCTransaction) => {
		if (!mortgage?.clientMappings) {
			return false;
		}

		const mappingsCount = mortgage?.clientMappings.filter(
			(x) => x.type === 'company' || x.type === 'person',
		).length;

		const guarantorCount = mortgage?.data?.guarantors?.length
			? mortgage?.data?.guarantors?.length
			: 0;
		const mortgagorCount = mortgage?.data?.mortgagors?.length
			? mortgage?.data?.mortgagors?.length
			: 0;
		const expectedMappingTotal = guarantorCount + mortgagorCount;

		return mappingsCount === expectedMappingTotal;
	};

	verifyPerson = (client: PersonMatterClient): string[] => {
		const issues: string[] = [];
		if (!isSet(client?.firstName)) {
			issues.push('Missing first name');
		}

		if (!isSet(client?.lastName)) {
			issues.push('Missing last name');
		}

		return issues;
	};

	verifyCompany = (client: BusinessMatterClient): string[] => {
		const issues: string[] = [];
		if (!isSet(client?.name)) {
			issues.push('Missing company name');
		}

		return issues;
	};

	buildTransactionData = (
		matter: Matter,
		transaction: LLCTransaction,
		matterMortgage: MatterMortgageWithAssociations,
		mortgagors: (
			| PersonMatterClientWithAssociations
			| BusinessMatterClientWithAssociations
		)[],
		guarantors: (
			| PersonMatterClientWithAssociations
			| BusinessMatterClientWithAssociations
		)[],
		tarionAdjustment: MatterAdjustmentTarion,
		nameFormatingSettings: NameFormattingSettings,
		matterTitles: MatterTitle[],
		primaryJurisdiction: Jurisdiction,
	): TransactionData => {
		const transactionType = this.getTransactionType(matter.fileType);
		const property = this.getProperty(
			matter,
			transaction,
			matterMortgage,
			tarionAdjustment,
			matterTitles,
			primaryJurisdiction,
		);

		const borrowersBuyersPerson = mortgagors.filter(
			(x) => x.type === ClientType.PERSON,
		) as PersonMatterClientWithAssociations[];

		const data: TransactionData = {
			transactionId: transaction.overview.transactionId,
			lawyerMatterNumber: matter.number?.substring(0, 24),
			closingDate: matter.closingDate,
			transactionType,
			mortgagors: mortgagors
				.map(
					(client) =>
						this.buildTransactionPerson(
							client,
							transaction,
							transaction.data.mortgagors,
							'mortgagor',
							nameFormatingSettings,
						) as Mortgagor,
				)
				.filter((c) => c !== null),
			properties: [property],
			attorneys: this.buildAttorneys(
				borrowersBuyersPerson,
				transaction.clientMappings,
				transaction.lender,
				nameFormatingSettings,
			),
		};

		if (
			matter?.fileType === MatterFileType.PURCHASE &&
			isSet(matter.purchasePrice) &&
			isNumber(matter.purchasePrice)
		) {
			data.purchasePrice = Number(centsToDollars(matter.purchasePrice));
		}

		if (transaction.data.guarantors && transaction.data.guarantors.length > 0) {
			const _guarantors = guarantors.filter((x) =>
				transaction.clientMappings.some((y) => y.closerClientId === x.id),
			);

			data.guarantors = _guarantors
				.map(
					(client) =>
						this.buildTransactionPerson(
							client,
							transaction,
							transaction.data.guarantors,
							'guarantor',
							nameFormatingSettings,
						) as Guarantor,
				)
				.filter((c) => c !== null);
		}

		const lender = Object.values(matterMortgage?.lenders ?? {})[0];

		const titleInsurancePolicies = this.buildTitleInsurancePolicies(
			matter,
			matterMortgage.priority,
			lender ? getMortgageLenderName(lender) : '',
		);

		if (Object.keys(titleInsurancePolicies)?.length > 0) {
			data.titleInsurancePolicies = titleInsurancePolicies;
		}

		const clientBrokerage = getClientBrokerageFromMatter(matter);

		if (clientBrokerage?.broker) {
			data.realEstateAgent = {
				firmName: clientBrokerage.name?.substring(0, 199),
				firstName: clientBrokerage.broker.firstName?.substring(0, 99) ?? '',
				lastName: clientBrokerage.broker.lastName?.substring(0, 99) ?? '',
				phone: clientBrokerage.broker.phoneNumber?.substring(0, 49) ?? '',
			};
		}

		if (matter?.otherPartyLawFirm?.lawyer) {
			data.vendorSolicitor = {
				firmName: matter?.otherPartyLawFirm?.name?.substring(0, 199) ?? '',
				firstName:
					matter?.otherPartyLawFirm?.lawyer?.firstName?.substring(0, 99) ?? '',
				lastName:
					matter?.otherPartyLawFirm?.lawyer?.lastName?.substring(0, 99) ?? '',
			};
		}

		return data;
	};

	buildAttorneys(
		mortgagors: PersonMatterClientWithAssociations[],
		allClientMappings: ClientMappings,
		lender: MortgageInstructionLender,
		nameFormatingSettings: NameFormattingSettings,
	): Attorney[] {
		const attorneys: Attorney[] = [];

		mortgagors.forEach((mortgagor) => {
			if (mortgagor.representation === RepresentationType.ATTORNEY) {
				const _attorneys = mortgagor.clientAssociations.filter((x) =>
					Object.keys(x.associations.types).includes(
						AssociatedContactType.POWER_OF_ATTORNEY,
					),
				);

				if (_attorneys?.length > 0) {
					const clientsAttorneys = _attorneys.map((x) => {
						let llcId;
						const clientMappings = allClientMappings.filter(
							(mapping) => mapping.closerClientId === mortgagor.id,
						);

						if (clientMappings.length === 1) {
							llcId = clientMappings[0].llcId;
						} else {
							const type: ClientMappingType =
								mortgagor.type === ClientType.PERSON ? 'person' : 'company';
							const mapping = clientMappings.find((_m) => _m.type === type);
							if (mapping) {
								llcId = mapping.llcId;
							} else {
								LogRocket.error(
									'Unable to find mapping for attorny',
									mortgagor.id,
								);
								throw new Error(
									`Unable to find attorny for mortgagor ${mortgagor.id}`,
								);
							}
						}

						return this.buildAttorney(
							x.client as PersonClient,
							llcId,
							lender,
							nameFormatingSettings,
						);
					});

					attorneys.push(...clientsAttorneys);
				}
			}
		});

		return attorneys;
	}

	buildAttorney(
		client: PersonClient,
		llcClientId: number,
		lender: MortgageInstructionLender,
		nameFormatingSettings: NameFormattingSettings,
	): Attorney {
		const attorney: Attorney = {
			fullName: clientLegalName(client, nameFormatingSettings),
			mortgagorID: llcClientId,
			identifications: this.buildLLCIdentifications(
				client,
				lender,
				nameFormatingSettings,
			),
		};

		if (isSet(client.dateOfBirth)) {
			attorney.birthDate = client.dateOfBirth;
		}

		if (isSet(client.occupation)) {
			attorney.occupation = client.occupation?.substring(0, 49);
		}

		return attorney;
	}

	getTransactionType(matterFileType: MatterFileType): 'REFINANCE' | 'PURCHASE' {
		if (matterFileType === MatterFileType.PURCHASE) {
			return 'PURCHASE';
		}

		return 'REFINANCE';
	}

	buildTitleInsurancePolicies(
		matter: Matter,
		priority: MatterMortgagePriority,
		lenderPolicyNameDefault: string,
	): TitleInsurancePolicy[] {
		const policies: TitleInsurancePolicy[] = [];

		if (!isSet(matter?.titleInsurance)) {
			LogRocket.info(
				'No title insurance policies set - will be unable to get final report',
			);

			return [];
		}

		if (!isSet(matter.titleInsurance?.companyName)) {
			LogRocket.error(
				'No title insurance company set - this needs to be set to access final report',
			);

			return [];
		}

		if (!isSet(this.titleInsuranceEffectiveDate)) {
			LogRocket.error(
				'No issued effective date found for title insurance - title insurance is not completed - need to complete order to access final report',
			);

			return [];
		}

		const _policies = GetMortgageTitleInsurancePolicies(
			priority,
			matter.titleInsurance?.policies,
		);

		_policies?.forEach((policy) => {
			const _policy = cloneDeep(policy);
			if (
				!isSet(_policy.name) &&
				_policy.type === TitleInsurancePolicyType.LENDER
			) {
				// if the policy name isn't set default to the lender name
				_policy.name = lenderPolicyNameDefault;
			}

			if (
				isSet(_policy.name) &&
				isSet(_policy.number) &&
				isSet(_policy.insuredAmount)
			) {
				policies.push({
					insuranceCompany:
						matter.titleInsurance?.companyName?.substring(0, 49) ?? '',
					policyType:
						_policy?.type === TitleInsurancePolicyType.OWNER
							? 'OWNER'
							: 'LENDER',
					policyNumber: _policy.number?.substring(0, 49) ?? '',
					policyDate: this.titleInsuranceEffectiveDate,
					nameOfInsured: _policy.name?.substring(0, 499) ?? '',
					insuredAmount: Number(centsToDollars(_policy.insuredAmount)),
				});
			} else {
				const errorMsg =
					'Unable to send policy to llc as one of policy name / number / insuranced amount is not set - this will stop the user being able to get the final report';
				console.log(errorMsg, _policy);
				LogRocket.error(errorMsg, _policy);
			}
		});

		return policies;
	}

	getProperty(
		matter: Matter,
		mortgage: LLCTransaction,
		matterMortgage: MatterMortgage,
		tarionAdjustment: MatterAdjustment,
		matterTitles: MatterTitle[],
		jurisdiction: Jurisdiction,
	): Property {
		const id = mortgage?.data?.properties[0]?.id;
		const mortgagePriority = mortgage?.data?.properties[0]?.mortgagePriority;

		const property: Property = {
			id,
			mortgagePriority,
		};

		this.setEstateType(property, matter);

		if (isAddressSet(matter?.propertyDetails?.address)) {
			const mortgagePropertyAddress = mortgage?.data?.properties[0]?.address;
			property.address = convertCloserAddressToLLCAddress(
				matter.propertyDetails.address,
				mortgagePropertyAddress,
			);
			property.address.province =
				mortgagePropertyAddress?.province ?? property?.address?.province;
			property.address.country =
				mortgagePropertyAddress?.country ?? property?.address?.country;
		}

		if (
			isSet(matter?.propertyDetails?.propertyType?.multiUnit) &&
			isSet(matter?.propertyDetails?.propertyType?.numberOfUnits)
		) {
			property.numberOfUnits =
				matter?.propertyDetails?.propertyType?.numberOfUnits?.toString();
		}

		// TODO: condo plan number: dwelling unit plan number
		const dwellingUnit =
			matter.propertyDetails?.propertyType?.type === PropertyType.CONDO
				? (matterTitles?.find(
						(x: CondoUnit) => x.unitType === UnitType.DWELLING_UNIT,
					) as CondoUnit)
				: (null as CondoUnit);

		if (dwellingUnit) {
			if (isSet(dwellingUnit?.unitNumber)) {
				property.condoUnitNumber = (
					dwellingUnit as CondoUnit
				).unitNumber.substring(0, 29);
			}

			if (isSet(dwellingUnit?.levelNumber)) {
				property.condoLevel = (dwellingUnit as CondoUnit).levelNumber.substring(
					0,
					49,
				);
			}

			if (isSet(dwellingUnit?.planName)) {
				property.condoPlanNumber = (
					dwellingUnit as CondoUnit
				).planName.substring(0, 49);
			}
		}

		// if (matterMortgage?.taxes) {
		// 	property.lenderCollectsPropertyTaxes = matterMortgage?.taxes !== null;
		// }

		const legalDescAddition = getLegalDescription(
			matter?.propertyDetails,
			matterTitles,
			this.isCondoUnit,
		);
		if (legalDescAddition) {
			property.legalDescription = legalDescAddition;
		}

		if (isSet(matter.propertyDetails.propertyType)) {
			property.occupancyType = matter.propertyDetails.propertyType.tenanted
				? 'RENTAL'
				: 'OWNER OCCUPIED';
		}

		if (isSet(matter?.propertyDetails?.propertyType?.type)) {
			property.isCondominium =
				matter.propertyDetails.propertyType.type === PropertyType.CONDO;
		}

		if (
			matter?.propertyDetails?.propertyType?.type === PropertyType.CONDO &&
			isSet(matter?.condoCorporationNumber)
		) {
			property.condoCorporationNumber =
				matter.condoCorporationNumber?.substring(0, 24);
		}

		if (tarionAdjustment?.id) {
			property.newHomeWarranty = true;
		} else {
			property.newHomeWarranty = false;
		}

		// if (propertyTax?.taxBill && propertyTax?.taxBill !== 'na') {
		// 	property.annualTaxAmount = centsToDollars(propertyTax.total);
		// }

		property.propertyIdentificationNumbers =
			GetRequestForFundsPins(matterTitles);

		property.lenderCollectsPropertyTaxes =
			matterMortgage?.lenderCollectsPropertyTaxes
				? matterMortgage?.lenderCollectsPropertyTaxes
				: false;

		if (isSet(matterMortgage?.registrationNumber)) {
			property.instrumentNumber = matterMortgage?.registrationNumber?.substring(
				0,
				49,
			);
		}

		if (isSet(matter?.propertyDetails?.propertyType?.status)) {
			property.newConstruction =
				matter.propertyDetails.propertyType.status === PropertyStatus.NEW;
		}

		if (isSet(jurisdiction?.name)) {
			property.municipality = jurisdiction?.name.substring(0, 100);
		}

		if (isSet(matterMortgage?.registrationDate)) {
			property.registrationDate = matterMortgage.registrationDate;
		}

		if (isSet(matter?.propertyDetails?.lro?.selected?.id)) {
			property.LRONumber = Number(matter.propertyDetails.lro.selected.id);
		}

		if (isSet(matter?.propertyDetails?.lro?.selected?.EOName)) {
			// this is a bandaid to get the latest registry office - the incorrect EO was mapped in the data
			const lro = LROData[matter.propertyDetails.lro.selected.id];
			property.registryOffice = lro.name;
		}

		if (isSet(matter?.propertyDetails?.propertyType?.status)) {
			property.newConstruction =
				matter.propertyDetails.propertyType.status === PropertyStatus.NEW;
		}

		const fireInsurancePolicy = this.buildFireInsurancePolicy(matter);
		if (fireInsurancePolicy) {
			property.fireInsurancePolicy = fireInsurancePolicy;
		}

		if (isSet(matter?.propertyDetails?.rollNumber)) {
			property.assessmentRollNumbers = [matter.propertyDetails.rollNumber];
		}

		return property;
	}

	setEstateType(property: Property, matter: Matter) {
		const validEstateTypes = ['FEE SIMPLE', 'LEASEHOLD', 'FREEHOLD'];
		const llcEstateType = closerEstateTypeToLLCType(
			matter.propertyDetails.interestEstate,
		);
		if (validEstateTypes.includes(llcEstateType)) {
			property.estateType = llcEstateType;
		} else {
			property.estateType = 'OTHER';
			property.otherEstateTypeDescription = llcEstateType;
		}
	}

	buildFireInsurancePolicy(matter: Matter) {
		if (
			matter?.fireInsurance &&
			isSet(matter?.fireInsurance?.fireInsuranceCompany?.name) &&
			isSet(matter?.fireInsurance?.policyNumber) &&
			isSet(matter?.fireInsurance?.coverageAmount) &&
			isSet(matter?.fireInsurance?.activeDate)
		) {
			const fireInsurancePolicy: FireInsurancePolicy = {
				insuranceCompanyName:
					matter.fireInsurance?.fireInsuranceCompany?.name?.substring(0, 99) ??
					'',
				policyNumber: matter.fireInsurance?.policyNumber?.substring(0, 49),
				policyActiveDate: matter.fireInsurance?.activeDate,
				insuranceAmount: centsToDollars(matter.fireInsurance?.coverageAmount),
			};

			if (isAddressSet(matter?.fireInsurance?.fireInsuranceCompany?.address)) {
				fireInsurancePolicy.address =
					matter.fireInsurance?.fireInsuranceCompany?.address.street?.substring(
						0,
						99,
					);
				fireInsurancePolicy.country =
					matter.fireInsurance?.fireInsuranceCompany?.address.country?.substring(
						0,
						49,
					);
				fireInsurancePolicy.postalCode =
					matter.fireInsurance?.fireInsuranceCompany?.address.postcode?.substring(
						0,
						7,
					);
				fireInsurancePolicy.city =
					matter.fireInsurance?.fireInsuranceCompany?.address.city?.substring(
						0,
						49,
					);
				fireInsurancePolicy.province =
					matter.fireInsurance?.fireInsuranceCompany?.address.province;
			}

			if (isSet(matter.fireInsurance?.fireInsuranceCompany?.address?.unit)) {
				fireInsurancePolicy.unitNumber =
					matter.fireInsurance?.fireInsuranceCompany?.address.unit;
			}

			if (isSet(matter.fireInsurance?.fireInsuranceCompany?.address?.street2)) {
				fireInsurancePolicy.address2 =
					matter.fireInsurance?.fireInsuranceCompany?.address.street2?.substring(
						0,
						99,
					);
			}

			if (isSet(matter.fireInsurance?.fireInsuranceBrokerage?.name)) {
				fireInsurancePolicy.broker =
					matter.fireInsurance?.fireInsuranceBrokerage?.name?.substring(0, 199);
			}

			if (isSet(matter.fireInsurance?.guaranteedReplacementCoverage)) {
				fireInsurancePolicy.guaranteedReplacementCoverage =
					matter.fireInsurance?.guaranteedReplacementCoverage;
			}

			if (isSet(matter.fireInsurance?.expiryDate)) {
				fireInsurancePolicy.expiryDate = matter.fireInsurance?.expiryDate;
			}

			return fireInsurancePolicy;
		}

		return null;
	}

	buildTransactionPerson(
		mortgagor:
			| PersonMatterClientWithAssociations
			| BusinessMatterClientWithAssociations,
		mortgage: LLCTransaction,
		llcPersons: MortgageInstructionPerson[],
		personType: 'mortgagor' | 'guarantor',
		nameFormatingSettings: NameFormattingSettings,
	): Mortgagor | Guarantor {
		const llcPersonType = `${personType}Type`;

		const clientMappings = mortgage.clientMappings.filter(
			(x) => x.closerClientId === mortgagor.id,
		);

		if (!clientMappings) {
			LogRocket.error(
				'LLC Guarantor - Unable to find person client mapping for client',
				mortgagor.id,
			);
			throw new Error(
				`LLC Guarantor - Unable to find person client mapping, mortgagor: ${mortgage.id}`,
			);
		}

		const clientMapping = clientMappings.find((client) =>
			llcPersons.some((x) => x.id === client.llcId),
		);

		if (!clientMapping) {
			return null;
		}

		const llcClient = llcPersons.find((x) => x.id === clientMapping.llcId);
		let base;

		if (clientMapping.type === 'person') {
			base = this.buildBaseClient(
				mortgagor as PersonMatterClientWithAssociations,
				Number(llcClient.id),
				llcClient.address,
				mortgage.lender,
				personType,
				nameFormatingSettings,
			);
		} else {
			base = this.buildCompanyClient(
				mortgagor as BusinessMatterClientWithAssociations,
				mortgage.clientMappings,
				Number(llcClient.id),
				llcClient.address,
				mortgage.lender,
				nameFormatingSettings,
			);
		}

		return {
			...base,
			[llcPersonType]: llcClient[llcPersonType],
		} as Mortgagor | Guarantor;
	}

	buildCompanyClient(
		mortgagor: BusinessMatterClientWithAssociations,
		clientMappings: ClientMappings,
		llcId: number,
		llcAddress: BaseTransactionAddress,
		lender: MortgageInstructionLender,
		nameFormatingSettings: NameFormattingSettings,
	): BaseClient {
		const client: BaseClient = {
			id: llcId,
			companyName: mortgagor.name,
		};

		if (isSet(mortgagor?.phone)) {
			client.phone = mortgagor.phone;
		}

		if (isSet(mortgagor?.provinceOfIncorporation)) {
			client.provinceOfIncorporation = mortgagor.provinceOfIncorporation;
		}

		const incorporation: Partial<Incorporation> = {};

		if (isSet(mortgagor?.corporationNumber)) {
			incorporation.registrationNumber =
				mortgagor?.corporationNumber?.substring(0, 49);
		}

		if (isSet(mortgagor?.businessActivity)) {
			incorporation.entityType = mortgagor?.businessActivity?.substring(0, 99);
		}

		if (isSet(mortgagor?.jurisdiction)) {
			incorporation.recordType = mortgagor?.jurisdiction?.substring(0, 99);
		}

		if (isSet(mortgagor?.recordSource)) {
			incorporation.electronicRecordSource = mortgagor?.recordSource?.substring(
				0,
				99,
			);
		}

		client.incorporation = incorporation as Incorporation;

		if (isAddressSet(mortgagor?.serviceAddress)) {
			client.address = convertCloserAddressToLLCAddress(
				mortgagor.serviceAddress,
				llcAddress,
			);
			client.address.province =
				llcAddress?.province ?? client?.address?.province;
			client.address.country =
				llcAddress?.country ?? client?.address?.country?.substring(0, 49);
		}

		const signingOfficers = mortgagor.clientAssociations.filter((x) =>
			Object.keys(x.associations.types).includes(
				AssociatedContactType.SIGNING_OFFICER,
			),
		);

		this.setPrimaryOfficerDetails(
			clientMappings,
			llcId,
			signingOfficers,
			client,
		);

		client.signatories = signingOfficers.map((x) =>
			this.buildLLCSignatory(
				x.client as PersonClient,
				lender,
				nameFormatingSettings,
			),
		);

		return client;
	}

	setPrimaryOfficerDetails(
		clientMappings: ClientMappings,
		llcId: number,
		signingOfficers: MatterClientAssociations[],
		client: BaseClient,
	) {
		let primaryOfficer: PersonClient;

		const primarySigningOfficerIds = clientMappings
			.filter((x) => x.type === 'signingOfficer' && x.llcId === llcId)
			.map((x) => x.closerClientId);

		if (primarySigningOfficerIds?.length > 1) {
			console.log(
				'LLC mapping - Multiple signing officers. This should not happen',
			);
			LogRocket.warn(
				'LLC mapping - Multiple signing officers. This should not happen',
			);
		}

		const primarySigningOfficerId =
			primarySigningOfficerIds?.length > 0 ? primarySigningOfficerIds[0] : null;

		if (primarySigningOfficerId) {
			primaryOfficer = signingOfficers.find(
				(x) => x.client.id === primarySigningOfficerId,
			)?.client as PersonClient;
		}

		if (!primaryOfficer) {
			primaryOfficer = signingOfficers.find(
				(x) => x.associations.primaryContact,
			)?.client as PersonClient;
		}

		if (primaryOfficer) {
			client.firstName = primaryOfficer?.firstName?.substring(0, 99) ?? '';
			client.lastName = primaryOfficer?.lastName?.substring(0, 49) ?? '';

			if (isSet(primaryOfficer?.middleNames)) {
				client.middleName = primaryOfficer?.middleNames?.substring(0, 49);
			}
		}
	}

	buildBaseClient(
		mortgagor: PersonMatterClientWithAssociations,
		llcId: number,
		llcAddress: BaseTransactionAddress,
		lender: MortgageInstructionLender,
		personType: 'mortgagor' | 'guarantor',
		nameFormatingSettings: NameFormattingSettings,
	): BaseClient {
		let client: BaseClient = {
			id: llcId,
			firstName: mortgagor.firstName?.substring(0, 99) ?? '',
			lastName: mortgagor.lastName?.substring(0, 49) ?? '',
		};

		if (isSet(mortgagor?.middleNames)) {
			client.middleName = mortgagor.middleNames?.substring(0, 49);
		}

		if (isSet(mortgagor?.phone)) {
			client.phone = mortgagor.phone?.substring(0, 49);
		}

		if (isSet(mortgagor?.work)) {
			client.businessPhone = mortgagor.work?.substring(0, 25);
		}

		if (isSet(mortgagor?.dateOfBirth)) {
			client.birthDate = mortgagor.dateOfBirth;
		}

		if (isSet(mortgagor?.occupation)) {
			client.occupation = mortgagor.occupation?.substring(0, 49);
		}

		if (isAddressSet(mortgagor?.serviceAddress)) {
			client.address = convertCloserAddressToLLCAddress(
				mortgagor.serviceAddress,
				llcAddress,
			);
			client.address.province =
				llcAddress?.province ?? client?.address?.province;
			client.address.country = llcAddress?.country ?? client?.address?.country;
		}

		if (mortgagor?.identification) {
			client.identifications = this.buildLLCIdentifications(
				mortgagor,
				lender,
				nameFormatingSettings,
			);
		}

		if (personType === 'mortgagor') {
			const spouse = this.buildSpouse(mortgagor);

			if (spouse) {
				client = { ...client, ...spouse };
			}
		}

		return client;
	}

	buildSpouse(
		mortgagor: PersonMatterClientWithAssociations,
	): Partial<BaseClient> {
		const baseClient: Partial<BaseClient> = {};

		if (
			mortgagor?.flaClient &&
			mortgagor?.fla?.spousalStatus === SpousalStatus.CONSENTING
		) {
			if (isSet(mortgagor?.flaClient?.firstName)) {
				baseClient.spouseFirstName = mortgagor?.flaClient?.firstName?.substring(
					0,
					99,
				);
			}
			if (isSet(mortgagor?.flaClient?.middleNames)) {
				baseClient.spouseMiddleName =
					mortgagor?.flaClient?.middleNames?.substring(0, 49);
			}
			if (isSet(mortgagor?.flaClient?.lastName)) {
				baseClient.spouseLastName = mortgagor?.flaClient?.lastName?.substring(
					0,
					49,
				);
			}
			if (isSet(mortgagor?.flaClient?.occupation)) {
				baseClient.spouseOccupation =
					mortgagor?.flaClient?.occupation?.substring(0, 49);
			}
		}

		return baseClient;
	}

	buildLLCSignatory = (
		client: PersonClient,
		lender: MortgageInstructionLender,
		nameFormatingSettings: NameFormattingSettings,
	): Signatory => {
		if (!(client && isSet(client?.firstName) && isSet(client?.lastName))) {
			return null;
		}

		const signatory: Signatory = {
			firstName: client.firstName?.substring(0, 99) ?? '',
			lastName: client.lastName?.substring(0, 49) ?? '',
		};

		if (isSet(client?.middleNames)) {
			signatory.middleName = client.middleNames?.substring(0, 49) ?? '';
		}

		if (isSet(client?.dateOfBirth)) {
			signatory.birthDate = client.dateOfBirth;
		}

		if (isSet(client?.occupation)) {
			signatory.occupation = client.occupation?.substring(0, 49);
		}

		if (client?.identification) {
			signatory.identifications = this.buildLLCIdentifications(
				client,
				lender,
				nameFormatingSettings,
			);
		}

		return signatory;
	};

	buildLLCIdentifications = (
		client: PersonClient,
		lender: MortgageInstructionLender,
		nameFormatingSettings: NameFormattingSettings,
	): LLCIdentifications[] => {
		if (!client?.identification) {
			return [];
		}

		const lenderType = getLLCLenderType(lender);

		const ids: LLCIdentifications[] = [];

		if (client.identification?.primary?.type) {
			const primaryId = this.buildIdentification(
				client.identification.primary,
				lenderType,
				client,
				nameFormatingSettings,
			);
			ids.push(primaryId);

			if (client.identification?.secondary?.type) {
				const secondaryId = this.buildIdentification(
					client.identification.secondary,
					lenderType,
					client,
					nameFormatingSettings,
				);
				ids.push(secondaryId);
			}
		}

		// Filters out null values
		return ids.filter((x) => !!x);
	};

	buildIdentification = (
		id: Identification,
		lenderType: LLCLenderType,
		client: PersonClient,
		nameFormatingSettings: NameFormattingSettings,
	): LLCIdentifications => {
		if (lenderType === LLCLenderType.MMS) {
			return null;
		}

		const _identification = Ids.find((x) => x.closerId === id.type);

		let idKey;

		switch (lenderType) {
			case LLCLenderType.BNS:
				idKey = _identification.BNSKey;
				break;
			case LLCLenderType.TD:
				idKey = _identification.TDKey;
				break;
		}

		// if there is no key mapping return null
		if (!idKey) {
			return null;
		}

		const llcIdentification: LLCIdentifications = {
			identificationType: idKey,
		};

		if (isSet(id?.number)) {
			llcIdentification.identificationNumber = id.number?.substring(0, 49);
		}

		if (isSet(id?.expirationDate)) {
			llcIdentification.expiryDate = id.expirationDate;
		}

		if (isSet(id?.jurisdiction)) {
			llcIdentification.issuingJurisdiction = id.jurisdiction?.substring(0, 99);
		}

		if (isSet(id?.country)) {
			llcIdentification.issuingCountry = id.country?.substring(0, 99);
		}

		if (isSet(id?.dateOfVerification)) {
			llcIdentification.verificationDate = id.dateOfVerification;
		}

		llcIdentification.nameVerified = isSet(id.verifiedBy);
		llcIdentification.fullName = isSet(id?.fullName)
			? id.fullName?.substring(0, 199)
			: clientLegalName(client, nameFormatingSettings)?.substring(0, 199);

		if (isSet(client.dateOfBirth)) {
			llcIdentification.birthDate = client?.dateOfBirth;
		}

		return llcIdentification;
	};

	private errorHandler = (err: unknown) => {
		console.log('ERROR SENDING DATA TO LLC - ', err);

		LogRocket.error(
			`Unable to access LLC portal - ${this.transaction.id}, fct source urn: ${this.transaction?.overview?.sourceFctUrn}, lender ref number: ${this.transaction?.overview?.lenderReferenceNumber}, lender code: ${this.transaction?.overview?.lenderCode}`,
			err,
		);

		if (
			err instanceof FirebaseError &&
			err.code === `functions/${ErrorCodes.FAILED_PRECONDITION}` &&
			err.message
		) {
			this.errorMessage_.set(err.message);
			return;
		}

		this.errorMessage_.set(
			'Unable to load LLC Portal. Please close the window and try again.',
		);
	};
}
